import { useCallback, useEffect, useState } from 'react';
import { Typography } from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';
import { Bell, X, XCircle } from 'lucide-react';

export interface NotificationProps {
  /** Ícone do tipo de notificação */
  icon?: React.ComponentType<{ className?: string }>;
  /** Título da notificação */
  title: string;
  /** Mensagem da notificação */
  message: string;
  /** Variante da notificação */
  variant?: 'error' | 'notification';
  /** Se deve desaparecer automaticamente */
  autoDismiss?: boolean;
  /** Tempo em milissegundos para auto-dismiss (padrão: 5000ms) */
  timeout?: number;
  /** Callback chamado quando a notificação é fechada */
  onClose?: () => void;
  /** Se deve aparecer persistente (não fecha automaticamente) */
  persistent?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

export function Notification({
  icon: CustomIcon,
  title,
  message,
  variant = 'notification',
  autoDismiss = true,
  timeout = 5000,
  onClose,
  persistent = false,
  className = ''
}: NotificationProps) {
  const { t } = useTranslate();
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    onClose?.();
  }, [onClose]);

  // Auto-dismiss logic
  useEffect(() => {
    if (!persistent && autoDismiss && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, timeout, persistent, isVisible, handleClose]);

  // Seleciona o ícone baseado na variant se não foi fornecido um custom
  const getDefaultIcon = () => {
    if (CustomIcon) {
      return CustomIcon;
    }
    return variant === 'error' ? XCircle : Bell;
  };

  const IconComponent = getDefaultIcon();

  // Cores baseadas na variant
  const getVariantStyles = () => {
    if (variant === 'error') {
      return {
        background: 'var(--color-error-100, #FFE3D6)',
        borderColor: 'border-red-300',
        iconColor: 'var(--text-dark, #191F2E)',
        textColor: 'var(--text-dark, #191F2E)'
      };
    }

    return {
      background: '#D3E5FE',
      borderColor: 'border-blue-300',
      iconColor: 'var(--text-dark, #191F2E)',
      textColor: 'var(--text-dark, #191F2E)'
    };
  };

  const styles = getVariantStyles();

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`
        ${className.includes('relative') ? 'relative' : 'fixed'} right-4 z-50
        w-[524px] h-[84px]
        rounded-md
        flex items-center justify-between
        px-4 py-4
        transition-all duration-300 ease-in-out
        ${className}
      `}
      style={{
        backgroundColor: styles.background,
        ...(className.includes('relative')
          ? {}
          : {
              top: '90%',
              transform: 'translateY(-100%)'
            }),
        boxShadow: '0px 6px 7px -2px #00000014'
      }}>
      {/* Ícone */}
      <div className={`flex-shrink-0 mr-4 ${styles.iconColor}`}>
        <IconComponent className="w-6 h-6" />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        <Typography variant="body-md-bold" color="dark" className="mb-1">
          {title}
        </Typography>
        <Typography
          variant="metadata-sm-regular"
          color="dark"
          className="truncate">
          {message}
        </Typography>
      </div>

      {/* Botão de fechar */}
      <button
        onClick={handleClose}
        className="
          flex-shrink-0 ml-3
          p-1 rounded-sm
          hover:bg-black/10
          transition-colors
          text-gray-600
        "
        style={{ color: styles.iconColor }}
        aria-label={t('common.notification.close', 'Close notification')}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
